import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Gift, HotelSample, Location, Notification, Watch } from '../../../assests/ExportImageIcon'
import DatePicker from '../../../componants/datePicker/DatePicker'
import Modal from '../../../componants/modal/Modal'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { parseVenueCapacity, pickListingPrice, VENUE_PRICING_TYPE_LABELS } from '../../../utils/venueFormat'
import { showToast } from '../../../componants/toast/toastService'
import useAppOptions from '../../../hooks/useAppOptions'
import './venueBookingDetail.css'
import '../venueModals.css'
import '../venueShared.css'

const RESPONSE_INFO = [
    { icon: Watch, tint: '#E3F0FF', title: 'Expected Response Time', text: 'Vendors typically respond within 24-48 hours with their proposals.' },
    { icon: Gift, tint: '#FFF4D6', title: 'Multiple Proposals', text: "You'll receive proposals from multiple vendors to compare and choose the best option." },
    { icon: Notification, tint: '#E6F6EF', title: 'Notification Alert', text: "We'll notify you via email and app notifications when vendors submit their proposals." },
]

const todayIso = () => new Date().toISOString().slice(0, 10)

const VenueBookingDetail = () => {
    const navigate = useNavigate()
    const { venueId } = useParams()
    const [searchParams] = useSearchParams()
    const [venue, setVenue] = useState(null)
    const [eventTypes, setEventTypes] = useState([])
    const [eventType, setEventType] = useState('')
    const [guestCount, setGuestCount] = useState(searchParams.get('guests') || '')
    const [pricingType, setPricingType] = useState('')
    const [startDate, setStartDate] = useState(searchParams.get('date') || '')
    const [endDate, setEndDate] = useState(searchParams.get('date') || '')
    const [durationHours, setDurationHours] = useState('')
    const [plateCount, setPlateCount] = useState('')
    const [selectedEventSlots, setSelectedEventSlots] = useState([])
    const [timeSlot, setTimeSlot] = useState('')
    const [minBudget, setMinBudget] = useState('')
    const [maxBudget, setMaxBudget] = useState(searchParams.get('budget') || '')
    const [notes, setNotes] = useState(() => {
        const bits = []
        const services = (searchParams.get('services') || '').replace(/_/g, ' ')
        if (services && services !== 'none') bits.push(`Need ${services} through event companies.`)
        const location = searchParams.get('location')
        if (location) bits.push(`Event location: ${location}.`)
        const kind = searchParams.get('kind')
        if (kind && kind !== 'any') bits.push(`Preferred venue type: ${kind}.`)
        return bits.join(' ')
    })
    const [submitting, setSubmitting] = useState(false)
    const [submittedOpen, setSubmittedOpen] = useState(false)
    const [submittedRequestId, setSubmittedRequestId] = useState('')
    const [errors, setErrors] = useState({})

    const clearError = (field) => {
        setErrors((prev) => {
            if (!(field in prev)) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }

    useEffect(() => {
        if (!venueId) return
        service
            .get(endpoints.venues.detail.replace('{id}', venueId), {}, { silent: true })
            .then(({ data }) => {
                const venueData = data?.data ?? data ?? null
                setVenue(venueData)
                // GET /users/venues/{id} now returns the event types THIS venue actually
                // supports — use that instead of the global /vendors/venues/events/ list
                // so the dropdown can't offer an event type the venue doesn't support.
                setEventTypes(Array.isArray(venueData?.events) ? venueData.events : [])
            })
            .catch(() => setVenue(null))
    }, [venueId])

    useEffect(() => {
        const wanted = (searchParams.get('event') || '').toLowerCase().replace(/_/g, ' ')
        if (!wanted || !eventTypes.length || eventType) return
        const matched = eventTypes.find((item) => String(item?.name || '').toLowerCase().includes(wanted))
        if (matched?.id != null) setEventType(String(matched.id))
    }, [eventTypes, searchParams, eventType])

    useEffect(() => {
        const guests = searchParams.get('guests')
        const date = searchParams.get('date')
        const budget = searchParams.get('budget')
        const minBudget = searchParams.get('min_budget')
        const pricing = searchParams.get('pricing_type')
        const time = searchParams.get('time')
        if (guests) setGuestCount(guests)
        if (date) {
            setStartDate(date)
            setEndDate(date)
        }
        if (budget) setMaxBudget(budget)
        if (minBudget) setMinBudget(minBudget)
        if (pricing) setPricingType(pricing)
        if (time) setTimeSlot(time)
        const services = (searchParams.get('services') || '').replace(/_/g, ' ')
        const location = searchParams.get('location')
        const kind = searchParams.get('kind')
        const bits = []
        if (services && services !== 'none') bits.push(`Need ${services} through event companies.`)
        if (location) bits.push(`Event location: ${location}.`)
        if (kind && kind !== 'any') bits.push(`Preferred venue type: ${kind}.`)
        if (bits.length) setNotes(bits.join(' '))
    }, [searchParams])

    // No rating field on GET /users/venues/{id} — never show a fake one.
    const summary = venue
        ? {
            // photo_gallery items are objects ({id, venue, image}), not
            // plain URL strings — extract .image before using as <img src>.
            image: (Array.isArray(venue.photo_gallery) && venue.photo_gallery[0]?.image) || venue.cover_image || HotelSample,
            name: venue.name || '',
            venueType: venue.category_name || '',
            // Real venue data present (even if capacity/price is "N/A")
            // should show that honestly (hidden / "—"), not a made-up
            // fallback number.
            capacity: parseVenueCapacity(venue.capacity),
            // Confirmed live 2026-09-21: this endpoint's own top-level
            // `price` field is literally "N/A" — real per-type prices live
            // in `pricings[]` (same fix as venueDetail.jsx's hero price).
            price: pickListingPrice(venue).display,
            priceUnit: pickListingPrice(venue).unit,
            location: venue.address_summary || '',
            // Confirmed live 2026-09-21: `distance_summary` comes back as
            // the literal string "N/A" — don't show that as a real value.
            locationNote: (venue.distance_summary && venue.distance_summary !== 'N/A') ? venue.distance_summary : '',
            amenities: (venue.amenities || []).map((item) => (typeof item === 'string' ? item : item?.name)).filter(Boolean).slice(0, 4),
        }
        : null

    // pricing_type is now required on the RFP — options are this venue's own
    // pricings[] (per_hour/per_day/per_event/per_plate), each carrying the base
    // price used below to estimate total_estimated_price. Labels come from the
    // admin-defined master list (GET /admin-panel/app-options/list?group=venue_pricing_type)
    // when available, falling back to the static map if that call hasn't resolved yet.
    const pricingTypeAppOptions = useAppOptions('venue_pricing_type')
    const pricingTypeLabel = (value) => (
        pricingTypeAppOptions.find((opt) => opt.value === value)?.label
        || VENUE_PRICING_TYPE_LABELS[value]
        || value
    )
    const pricingOptions = Array.isArray(venue?.pricings) ? venue.pricings : []
    const selectedPricingRow = pricingOptions.find((row) => row.pricing_type === pricingType) || null
    // Only "Per Day" bookings span a date range — every other pricing type is a
    // single-day booking, so the form asks for one "Booking Date" instead of a
    // Start/End pair, and end_date is kept mirrored to start_date behind the
    // scenes so the payload still satisfies the API without asking the user for it.
    const isPerDay = pricingType === 'per_day'

    const [eventSlotOptions, setEventSlotOptions] = useState([])
    // GET /users/venues/{id}/free-slots — response shape confirmed live
    // 2026-08-31: {data: [{id, slot_name, start_time, end_time}]}, matching the
    // embedded pricings[].event_slots shape exactly. 2026-09-01: Swagger only
    // documents `id` as a path param (no `date`), but per Jatin the backend
    // also wants `date`, and `id` repeated as a query param alongside it — so
    // `id` is sent both ways (path substitution + query) and `date` as query.
    // Only fetches once a booking date is picked, refetches if the date changes.
    // Neither the `id`-in-query nor the `date` param is Swagger-confirmed —
    // verify against a real request/response and adjust if the backend rejects
    // either.
    useEffect(() => {
        if (pricingType !== 'per_event' || !venueId || !startDate) {
            setEventSlotOptions([])
            return
        }
        let cancelled = false
        service
            .get(endpoints.venues.freeSlots.replace('{id}', venueId), { id: venueId, date: startDate }, { silent: true })
            .then(({ data }) => {
                if (cancelled) return
                const raw = Array.isArray(data?.data) ? data.data : []
                setEventSlotOptions(
                    Array.from(new Map(raw.map((slot) => [`${slot.slot_name}-${slot.start_time}-${slot.end_time}`, slot])).values())
                )
            })
            .catch(() => {
                if (!cancelled) setEventSlotOptions([])
            })
        return () => {
            cancelled = true
        }
    }, [pricingType, venueId, startDate])

    // Keep end_date mirrored to start_date whenever this isn't a per_day booking.
    useEffect(() => {
        if (!isPerDay) setEndDate(startDate)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPerDay, startDate])

    // POST /vendors/user_app/request-proposal/estimate-price — real price
    // estimate from the backend (replaces the earlier client-side weekend/
    // peak-season guess). Only the fields relevant to the selected pricing
    // type are sent, and only once all of that type's required fields are
    // filled in — everything else stays null (no call fired).
    const buildEstimatePayload = () => {
        if (!venueId || !pricingType || !startDate) return null
        const payload = { venue: Number(venueId), pricing_type: pricingType, start_date: startDate }
        if (pricingType === 'per_day') {
            if (!endDate) return null
            payload.end_date = endDate
        } else if (pricingType === 'per_hour') {
            if (!durationHours) return null
            payload.duration_hours = Number(durationHours)
        } else if (pricingType === 'per_event') {
            if (!selectedEventSlots.length) return null
            payload.selected_event_slots = selectedEventSlots
        } else if (pricingType === 'per_plate') {
            if (!plateCount) return null
            payload.plate_count = Number(plateCount)
        } else {
            return null
        }
        return payload
    }
    const estimatePayload = buildEstimatePayload()

    const [estimatedPrice, setEstimatedPrice] = useState(0)
    const [estimatedCurrency, setEstimatedCurrency] = useState('INR')
    const [estimating, setEstimating] = useState(false)
    const estimateRequestIdRef = useRef(0)

    useEffect(() => {
        if (!estimatePayload) {
            setEstimatedPrice(0)
            setEstimating(false)
            return undefined
        }
        const requestId = ++estimateRequestIdRef.current
        const timer = setTimeout(() => {
            setEstimating(true)
            service
                .post(endpoints.venues.estimatePrice, estimatePayload, { silent: true })
                .then(({ data }) => {
                    if (requestId !== estimateRequestIdRef.current) return
                    const body = data?.data ?? data ?? {}
                    setEstimatedPrice(Number(body.total_estimated_price) || 0)
                    setEstimatedCurrency(body.currency || selectedPricingRow?.currency || 'INR')
                })
                .catch(() => {
                    if (requestId === estimateRequestIdRef.current) setEstimatedPrice(0)
                })
                .finally(() => {
                    if (requestId === estimateRequestIdRef.current) setEstimating(false)
                })
        }, 400)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pricingType, startDate, endDate, durationHours, plateCount, selectedEventSlots])

    const toggleEventSlot = (slotId) => {
        setSelectedEventSlots((prev) => (
            prev.includes(slotId) ? prev.filter((id) => id !== slotId) : [...prev, slotId]
        ))
    }

    const failField = (field, message) => {
        setErrors({ [field]: message })
        showToast(message, 'error')
    }

    const handleSubmit = () => {
        setErrors({})

        if (!eventType) {
            failField('eventType', 'Please select an event type')
            return
        }
        if (!guestCount) {
            failField('guestCount', 'Please enter the expected guest count')
            return
        }
        if (venue?.min_guests != null && Number(guestCount) < venue.min_guests) {
            failField('guestCount', `Expected guest count cannot be below the venue's minimum (${venue.min_guests})`)
            return
        }
        if (venue?.max_guests != null && Number(guestCount) > venue.max_guests) {
            failField('guestCount', `Expected guest count cannot exceed the venue's maximum allowed (${venue.max_guests})`)
            return
        }
        if (!pricingType) {
            failField('pricingType', 'Please select a pricing option')
            return
        }
        if (!startDate || (isPerDay && !endDate)) {
            failField('startDate', isPerDay ? 'Please select the event start and end date' : 'Please select the booking date')
            return
        }
        if (new Date(endDate) < new Date(startDate)) {
            failField('endDate', 'End date cannot be before start date')
            return
        }
        if (pricingType === 'per_hour' && !durationHours) {
            failField('durationHours', 'Please enter the expected duration in hours')
            return
        }
        if (pricingType === 'per_plate' && !plateCount) {
            failField('plateCount', 'Please enter the expected plate count')
            return
        }
        if (pricingType === 'per_event' && selectedEventSlots.length === 0) {
            failField('selectedEventSlots', 'Please select at least one event slot')
            return
        }
        if (pricingType !== 'per_event' && !timeSlot) {
            failField('timeSlot', 'Please select a preferred time')
            return
        }
        if (submitting) return

        // "Preferred Time" is hidden for Per Event (the event slots already carry
        // their own timing) but the backend rejects a blank preferred_time
        // regardless of pricing type — fall back to the first selected slot's
        // start time.
        const effectiveTimeSlot = pricingType === 'per_event'
            ? (eventSlotOptions.find((slot) => slot.id === selectedEventSlots[0])?.start_time || timeSlot)
            : timeSlot

        // Only the field for the SELECTED pricing_type goes in the payload —
        // sending e.g. duration_hours: 0 for a per_day booking triggered a
        // real 400 ("Duration Hours: Ensure this value is greater than or
        // equal to 1."), the backend validates it even when irrelevant.
        const typeSpecificField = (
            pricingType === 'per_hour' ? { duration_hours: Number(durationHours) }
                : pricingType === 'per_plate' ? { plate_count: Number(plateCount) }
                    : pricingType === 'per_event' ? { selected_event_slots: selectedEventSlots }
                        : {}
        )

        setSubmitting(true)
        service
            .post(endpoints.venues.requestProposal, {
                venue: Number(venueId),
                event_type: Number(eventType),
                expected_guest_count: Number(guestCount),
                start_date: startDate,
                end_date: endDate,
                preferred_time: effectiveTimeSlot,
                pricing_type: pricingType,
                total_estimated_price: String(estimatedPrice),
                budget_min: minBudget,
                budget_max: maxBudget,
                additional_requirements: notes,
                ...typeSpecificField,
            })
            .then(({ data }) => {
                // Confirmed live 2026-09-21: {id, chat_room_id, request_id,
                // status, is_booking, pricing_type, total_estimated_price}.
                // "View Proposals" stays on the generic /venue-booking-confirm
                // list (per user request), so only request_id is surfaced.
                const body = data?.data ?? data ?? {}
                setSubmittedRequestId(body.request_id || '')
                setSubmittedOpen(true)
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSubmitting(false))
    }

    return (
        <>
            <section className='flightBookingDetails commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            {summary ? (
                                <div className='hotelListItem venueProposalSummary'>
                                    <div className='hotelCard'>
                                        <div className='hotelCard__media'>
                                            <img src={summary.image} alt={summary.name} />
                                        </div>

                                        <div className='hotelCard__info'>
                                            <div className='hotelCard__topRow'>
                                                <div>
                                                    {/* No rating field on GET /users/venues/{id} — nothing real to show. */}
                                                    <div className='hotelCard__name'>{summary.name}</div>
                                                    {summary.venueType ? <div className='hotelCard__locationNote'>{summary.venueType}</div> : null}
                                                    {summary.capacity ? <div className='hotelCard__locationNote'>Capacity: {summary.capacity} Guests</div> : null}
                                                </div>
                                                <div className='hotelCard__price'>
                                                    <div className='hotelCard__priceValue'>{summary.price}</div>
                                                    {summary.priceUnit ? <div className='hotelCard__priceLabel'>Per {summary.priceUnit}</div> : null}
                                                </div>
                                            </div>

                                            {(summary.location || summary.locationNote || summary.amenities.length > 0) && (
                                                <div className='hotelCard__bottomRow mt-4'>
                                                    {(summary.location || summary.locationNote) && (
                                                        <div className='hotelCard__location'>
                                                            <img src={Location} alt="" />
                                                            <div>
                                                                {summary.location && <div className='hotelCard__locationName'>{summary.location}</div>}
                                                                {summary.locationNote && <div className='hotelCard__locationNote'>{summary.locationNote}</div>}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {summary.amenities.length > 0 && (
                                                        <div className='hotelCard__amenities'>
                                                            {summary.amenities.map((amenity) => (
                                                                <span className='hotelCard__amenityPill' key={amenity}>{amenity}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className='fare-extra-hint'>Loading venue...</p>
                            )}
                        </div>

                        <div className='col-md-12'>
                            <div className='bsBox venueProposalForm'>
                                <div className='bsSectionTitle mb-3'>Request For Proposal</div>

                                <div className='travellerFieldRow'>
                                    <div className={`travellerField ${errors.eventType ? 'travellerField--error' : ''}`}>
                                        <label>Event Type</label>
                                        <select
                                            value={eventType}
                                            onChange={(e) => { setEventType(e.target.value); clearError('eventType') }}
                                        >
                                            <option value=''>Select Event Type</option>
                                            {eventTypes.map((type) => (
                                                <option key={type.id} value={type.id}>{type.name}</option>
                                            ))}
                                        </select>
                                        {errors.eventType && <span className='travellerFieldError'>{errors.eventType}</span>}
                                    </div>
                                    <div className={`travellerField ${errors.guestCount ? 'travellerField--error' : ''}`}>
                                        <label>Expected Guest Count</label>
                                        <input
                                            type='number'
                                            min={venue?.min_guests || 1}
                                            max={venue?.max_guests || undefined}
                                            placeholder='Enter number of guests'
                                            value={guestCount}
                                            onChange={(e) => { setGuestCount(e.target.value); clearError('guestCount') }}
                                        />
                                        {errors.guestCount && <span className='travellerFieldError'>{errors.guestCount}</span>}
                                    </div>
                                    <div className={`travellerField ${errors.pricingType ? 'travellerField--error' : ''}`}>
                                        <label>Pricing Option</label>
                                        <select
                                            value={pricingType}
                                            onChange={(e) => { setPricingType(e.target.value); clearError('pricingType') }}
                                        >
                                            <option value=''>Select Pricing Option</option>
                                            {pricingOptions.map((row) => (
                                                <option key={row.pricing_type} value={row.pricing_type}>
                                                    {pricingTypeLabel(row.pricing_type)}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.pricingType && <span className='travellerFieldError'>{errors.pricingType}</span>}
                                    </div>
                                </div>

                                <div className='travellerFieldRow'>
                                    <div className={`travellerField ${errors.startDate ? 'travellerField--error' : ''}`}>
                                        <label>{isPerDay ? 'Start Date' : 'Booking Date'}</label>
                                        <DatePicker
                                            min={todayIso()}
                                            value={startDate}
                                            onChange={(value) => { setStartDate(value); clearError('startDate') }}
                                        />
                                        {errors.startDate && <span className='travellerFieldError'>{errors.startDate}</span>}
                                    </div>
                                    {isPerDay && (
                                        <div className={`travellerField ${errors.endDate ? 'travellerField--error' : ''}`}>
                                            <label>End Date</label>
                                            <DatePicker
                                                min={startDate || todayIso()}
                                                value={endDate}
                                                onChange={(value) => { setEndDate(value); clearError('endDate') }}
                                            />
                                            {errors.endDate && <span className='travellerFieldError'>{errors.endDate}</span>}
                                        </div>
                                    )}
                                    {pricingType !== 'per_event' && (
                                        <div className={`travellerField ${errors.timeSlot ? 'travellerField--error' : ''}`}>
                                            <label>Preferred Time</label>
                                            <input
                                                type='time'
                                                value={timeSlot}
                                                onChange={(e) => { setTimeSlot(e.target.value); clearError('timeSlot') }}
                                            />
                                            {errors.timeSlot && <span className='travellerFieldError'>{errors.timeSlot}</span>}
                                        </div>
                                    )}
                                </div>

                                {pricingType === 'per_hour' && (
                                    <div className='travellerFieldRow'>
                                        <div className={`travellerField ${errors.durationHours ? 'travellerField--error' : ''}`}>
                                            <label>Duration (Hours)</label>
                                            <input
                                                type='number'
                                                min={1}
                                                placeholder='Enter expected hours'
                                                value={durationHours}
                                                onChange={(e) => { setDurationHours(e.target.value); clearError('durationHours') }}
                                            />
                                            {errors.durationHours && <span className='travellerFieldError'>{errors.durationHours}</span>}
                                        </div>
                                    </div>
                                )}

                                {pricingType === 'per_plate' && (
                                    <div className='travellerFieldRow'>
                                        <div className={`travellerField ${errors.plateCount ? 'travellerField--error' : ''}`}>
                                            <label>Plate Count</label>
                                            <input
                                                type='number'
                                                min={1}
                                                placeholder='Enter expected plate count'
                                                value={plateCount}
                                                onChange={(e) => { setPlateCount(e.target.value); clearError('plateCount') }}
                                            />
                                            {errors.plateCount && <span className='travellerFieldError'>{errors.plateCount}</span>}
                                        </div>
                                    </div>
                                )}

                                {pricingType === 'per_event' && !startDate && (
                                    <div className='travellerFieldRow'>
                                        <p className='travellerFieldNote'>Select a booking date to see available event slots.</p>
                                    </div>
                                )}

                                {pricingType === 'per_event' && startDate && eventSlotOptions.length > 0 && (
                                    <div className='travellerFieldRow'>
                                        <div className={`travellerField ${errors.selectedEventSlots ? 'travellerField--error' : ''}`}>
                                            <label>Event Slots</label>
                                            <div className='venueEventChips'>
                                                {eventSlotOptions.map((slot) => (
                                                    <span
                                                        key={slot.id}
                                                        className={`venueEventChip venueEventChip--selectable ${selectedEventSlots.includes(slot.id) ? 'active' : ''}`}
                                                        onClick={() => { toggleEventSlot(slot.id); clearError('selectedEventSlots') }}
                                                    >
                                                        {slot.slot_name} {slot.start_time}–{slot.end_time}
                                                    </span>
                                                ))}
                                            </div>
                                            {errors.selectedEventSlots && <span className='travellerFieldError'>{errors.selectedEventSlots}</span>}
                                        </div>
                                    </div>
                                )}

                                {selectedPricingRow && estimatePayload && (
                                    <div className='travellerFieldRow'>
                                        <div className='travellerField'>
                                            <label>Estimated Price</label>
                                            <div className='venueEstimatedPrice'>
                                                {estimating ? 'Calculating...' : `${estimatedCurrency} ${estimatedPrice.toLocaleString('en-IN')}`}
                                            </div>
                                            <span className='travellerFieldNote'>Final price will be confirmed by the vendor in their proposal.</span>
                                        </div>
                                    </div>
                                )}

                                <div className='travellerFieldRow'>
                                    <div className='travellerField'>
                                        <label>Budget Range</label>
                                        <div className='venueBudgetRange'>
                                            <input
                                                type='number'
                                                min={0}
                                                placeholder='Min'
                                                value={minBudget}
                                                onChange={(e) => setMinBudget(e.target.value)}
                                            />
                                            <input
                                                type='number'
                                                min={0}
                                                placeholder='Max'
                                                value={maxBudget}
                                                onChange={(e) => setMaxBudget(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className='travellerFieldRow'>
                                    <div className='travellerField'>
                                        <label>Additional Requirements</label>
                                        <textarea
                                            className='venueProposalTextarea'
                                            placeholder='Any special requests or requirements...'
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='col-md-12'>
                            <button type='button' className='commonBtn w-100' disabled={submitting} onClick={handleSubmit}>
                                {submitting ? 'Submitting...' : 'Submit Request'}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <Modal open={submittedOpen} onClose={() => setSubmittedOpen(false)} dismissible={false}>
                <div className='venueModal'>
                    <div className='venueModal__icon venueModal__icon--info'>
                        <img src={Location} alt="" width={26} height={26} />
                    </div>
                    <div className='venueModal__title'>Request Submitted Successfully!</div>
                    <p className='venueModal__subtitle'>
                        Thank you for your detailed information. Vendors will review your requirements and share their proposals soon.
                    </p>
                    {submittedRequestId && (
                        <p className='hotelCard__locationNote'>Request ID: {submittedRequestId}</p>
                    )}

                    <div className='venueModal__features'>
                        {RESPONSE_INFO.map((item) => (
                            <div className='venueModal__feature' key={item.title}>
                                <span className='venueModal__featureIconWrap' style={{ background: item.tint }}>
                                    <img src={item.icon} alt="" className='venueModal__featureIcon' />
                                </span>
                                <div>
                                    <div className='venueModal__featureTitle'>{item.title}</div>
                                    <div className='venueModal__featureText'>{item.text}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className='venueModal__actions venueModal__actions--column'>
                        <button type='button' className='commonBtn' onClick={() => navigate('/venue-booking-confirm')}>
                            View Proposals
                        </button>
                        <button type='button' className='venueModalBtn' onClick={() => navigate('/venue-list')}>
                            Back to Home
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    )
}

export default VenueBookingDetail
